// Cookie helpers
function getCookie(name: string): string | null {
  if (typeof document === 'undefined') return null
  const value = `; ${document.cookie}`
  const parts = value.split(`; ${name}=`)
  if (parts.length === 2) {
    const cookieValue = parts.pop()?.split(';').shift()
    return cookieValue ? decodeURIComponent(cookieValue) : null
  }
  return null
}

function setCookie(name: string, value: string, days = 90): void {
  if (typeof document === 'undefined') return
  const expires = new Date(Date.now() + days * 864e5).toUTCString()
  document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/; SameSite=Lax`
}

function getSgtmPreviewToken(): string | null {
  if (typeof window === 'undefined') return null
  const url = new URL(window.location.href)
  // First check URL parameter
  const urlToken = url.searchParams.get('sgtm_preview')
  if (urlToken) return urlToken
  // Fallback to cookie if not in URL
  return getCookie('sgtm_preview')
}

function getAllCookies(): Record<string, string> {
  if (typeof document === 'undefined') return {}
  return document.cookie
    .split('; ')
    .filter(Boolean)
    .reduce((acc, kv) => {
      const idx = kv.indexOf('=')
      const k = idx > -1 ? kv.slice(0, idx) : kv
      const v = idx > -1 ? kv.slice(idx + 1) : ''
      acc[k] = decodeURIComponent(v)
      return acc
    }, {} as Record<string, string>)
}

// Store click ids as 1p cookies so server can use them later
function persistClickIds(): void {
  if (typeof window === 'undefined') return
  const url = new URL(window.location.href)
  const clickIdParams = [
    'gclid',
    'gbraid',
    'wbraid',
    'fbclid',
    'ttclid',
    'msclkid',
    'twclid',
    'sccid', // snap
    'ob_click_id', // outbrain sometimes
    'tblci', // taboola often uses tblci
  ]

  clickIdParams.forEach((p) => {
    const val = url.searchParams.get(p)
    if (val) setCookie(`cid_${p}`, val, 30)
  })

  // Also persist UTMs
  const utmParams = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term']
  utmParams.forEach((p) => {
    const val = url.searchParams.get(p)
    if (val) {
      // Remove 'utm_' prefix for cookie name to avoid duplication
      const cookieName = p.startsWith('utm_') ? p : `utm_${p}`
      setCookie(cookieName, val, 30)
    }
  })

  // Persist Everflow parameters
  const everflowParams = [
    'uid',
    'oid',
    'affid',
    'sub1',
    'sub2',
    'sub3',
    'sub4',
    'sub5',
    'source_id',
    '_ef_transaction_id',
  ]
  everflowParams.forEach((p) => {
    const val = url.searchParams.get(p)
    if (val) setCookie(`ef_${p}`, val, 30)
  })

  // Persist sGTM preview token
  const sgtmPreview = url.searchParams.get('sgtm_preview')
  if (sgtmPreview) {
    setCookie('sgtm_preview', sgtmPreview, 30)
  }
}

// Ensure visitor ID exists
function ensureVisitorId(): string {
  const cookieName = 'ts_vid'
  let vid = getCookie(cookieName)
  if (!vid) {
    vid =
      (typeof crypto !== 'undefined' && crypto.randomUUID?.()) || `${Date.now()}_${Math.random().toString(16).slice(2)}`
    setCookie(cookieName, vid, 365)
  }
  return vid
}

interface SendEventOptions {
  event_id?: string
  cookieAllowlist?: string[]
}

export const useTracking = () => {
  const sendEventToBackend = async (
    eventName: string,
    eventData: Record<string, any> = {},
    options: SendEventOptions = {}
  ) => {
    if (typeof window === 'undefined') return

    const visitorId = ensureVisitorId()
    const authStore = useAuthStore()

    // Optional: limit cookies you send (privacy + payload size)
    const cookieAllowlist = options.cookieAllowlist || null
    const allCookies = getAllCookies()
    const cookies = cookieAllowlist
      ? Object.fromEntries(Object.entries(allCookies).filter(([k]) => cookieAllowlist.includes(k)))
      : allCookies

    const payload = {
      event_name: eventName,
      event_id:
        options.event_id ||
        (typeof crypto !== 'undefined' && crypto.randomUUID?.()) ||
        `${Date.now()}_${Math.random()}`,
      event_time: Math.floor(Date.now() / 1000),

      // your app-level identity / join key
      visitor_id: visitorId,

      // flexible event data (purchase, lead, etc.)
      ...eventData,

      // universal context
      context: {
        user_agent: navigator.userAgent,
        page_location: window.location.href,
        page_referrer: document.referrer || null,
        language: navigator.language || null,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || null,
        screen: `${window.screen?.width || 0}x${window.screen?.height || 0}`,
      },

      // identifiers = cookies + stored click ids in 1p cookies
      identifiers: {
        // keep your existing ones for convenience
        ga_client_id: getCookie('_ga') ? getCookie('_ga')!.substring(6) : null,
        fbp: getCookie('_fbp') || null,
        fbc: getCookie('_fbc') || null,

        // all cookies or allowlisted cookies
        cookies,

        // click ids persisted as cookies
        click_ids: {
          ...Object.fromEntries(Object.entries(allCookies).filter(([k]) => k.startsWith('cid_'))),
          b_click_id: authStore.user?.bclickId || null,
        },

        utm: Object.fromEntries(Object.entries(allCookies).filter(([k]) => k.startsWith('utm_'))),
      },
    }

    try {
      const headers: Record<string, string> = { 'Content-Type': 'application/json' }

      // Add sGTM preview header if preview token exists
      const previewToken = getSgtmPreviewToken()
      if (previewToken) {
        headers['x-gtm-server-preview'] = previewToken
      }

      await $fetch('/api/tracking/event', {
        method: 'POST',
        headers,
        body: payload,
        keepalive: true,
      })
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error sending tracking event:', error)
    }
  }

  return {
    getCookie,
    setCookie,
    getAllCookies,
    persistClickIds,
    ensureVisitorId,
    sendEventToBackend,
  }
}
